import { max } from '@vee-validate/rules';
import { defineRule } from 'vee-validate';
import {dateFormatter} from "@/components/Common/Formatters.js";

// addRowDataToRule passes the row as rowData.
defineRule('required-if-open-status', (value, { isOpenStatus, rowData: row }, ctx) => {
    if (!isOpenStatus(row)) {
        return true;
    }
    if (value) return true;
    return `${ctx?.field || 'This field'} is required when the status is Open.`;
});

export default {
    data() {
        return {
            webCatalogConfiguration: {
                type: 'webcatalogs',
                label: 'Web Catalog',
                importModes: [
                    {value:'add', text:'Add New (Skip when the catalog name is already present)'},
                    {value:'update', text:'Update (Skip when the catalog name is not present, update when found)'},
                    {value:'addAndUpdate', text:'Add and Update'}
                ],
                sampleNotes: [
                    'Catalog Name accepts numbers, letters and special characters',
                    'Status is required and must be Open, Closed, Inactive, or Hold',
                    'Start Date is required when the Status is Open, and optional for Closed, Inactive, or Hold',
                    'Dates are expected to be in YYYY-MM-DD format.',
                    'Catalog Group is optional, but when populated it must match an existing web catalog group name. The catalog is added to that group'
                ],
                fields: [
                    {
                        key: 'CatalogName',
                        label: 'Catalog Name',
                        hasErrorCalculator: value => {
                            return (this.isEmpty(value) || max(value, { length: 255 }) !== true);
                        },
                        rules: {
                            max: {length: 255},
                            required: true
                        },
                        samples: ['"Halloween"','"Men\'s Hats"','"Sporting Goods"']
                    },
                    {
                        key: 'Description',
                        label: 'Description',
                        hasErrorCalculator: value => {
                            return this.isEmpty(value);
                        },
                        rules: 'required',
                        samples: ['The Scariest Spooky Decorations', 'Something for a Man\'s Wardrobe', 'Outdoors and More']
                    },
                    {
                        key: 'Status',
                        label: 'Status',
                        hasErrorCalculator: value => {
                            return !this.matchesExistingObject(value, true);
                        },
                        dataType: 'catalogStatuses',
                        rules: 'min_value:0|required',
                        samples: ['Open', 'Closed', 'Inactive', 'Hold']
                    },
                    {
                        key: 'StartDate',
                        label: 'Start Date',
                        // Open catalogs require a valid start date
                        hasErrorCalculator: (value, row) => {
                            if (!this.isOpenStatus(row)) {
                                return false;
                            }
                            return this.isEmpty(value) || !this.isValidDate(value);
                        },
                        // Statuses other than Open have an optional start date
                        hasWarningCalculator: (value, row) => {
                            if (this.isOpenStatus(row)) {
                                return false;
                            }
                            return !this.isEmpty(value) && !this.isValidDate(value);
                        },
                        formatter: dateFormatter,
                        rules: {
                            'required-if-open-status': {
                                isOpenStatus: this.isOpenStatus
                            },
                            'valid-date': {
                                isValidDate: this.isValidDate,
                                propertyName: 'StartDate'
                            }
                        },
                        inputType: 'date',
                        addRowDataToRule: ['required-if-open-status', 'valid-date'],
                        warningDefault: null,
                        samples: [
                            '"2030-01-24"',
                            '""',
                            '"2033-12-25"'
                        ]
                    },
                    {
                        key: 'EndDate',
                        label: 'End Date',
                        hasWarningCalculator: value => {
                            return !this.isEmpty(value) && !this.isValidDate(value);
                        },
                        formatter: dateFormatter,
                        rules: {
                            'valid-date': {
                                isValidDate: this.isValidDate,
                                propertyName: 'EndDate'
                            }
                        },
                        inputType: 'date',
                        addRowDataToRule: 'valid-date',
                        warningDefault: null,
                        samples: [
                            '"2030-12-25"',
                            '""',
                            '"2035-01-01"'
                        ]
                    },
                    {
                        key: 'CatalogGroup',
                        label: 'Catalog Group',
                        hasErrorCalculator: value => {
                            return (
                                !this.isEmpty(value) &&
                                !this.matchesExistingObject(value)
                            );
                        },
                        dataType: 'catalogGroups',
                        rules: 'min_value:0',
                        samples: ['"Seasonal"', '""', '""']
                    },
                ]
            }
        };
    },
    computed: {
        openCatalogStatusId() {
            const openStatus = (this.selectListOptions.catalogStatuses ?? [])
                .find(x => x.text?.toLowerCase() === 'open');
            return openStatus?.value ?? null;
        }
    },
    methods: {
        isOpenStatus(row) {
            // A blank or unmatched status is flagged on the Status column.
            const statusId = row?.StatusId;
            if (this.isEmpty(statusId) || statusId === -1) {
                return false;
            }
            return statusId === this.openCatalogStatusId;
        }
    }
};
